/***
 *
 *   PRO CREDITS DASHBOARD
 *   Cross-account portfolio view of the credit system (CORE-3858)
 *
 **********/

import { Fragment, useState } from 'react';
import { Animate, Icon, Badge, useAPI } from 'components/lib';
import { useNavigate } from 'react-router-dom';
import { convertToLocalTime } from 'utility/helper';
import { rowsToCsv, downloadCsv } from 'utility/csv';

const ADJUSTMENTS_CSV_COLUMNS = [
  { key: 'accountName', label: 'Account' },
  { key: 'targetLabel', label: 'Target' },
  { key: 'amount', label: 'Credits' },
  { key: 'reasonLabel', label: 'Reason' },
  { key: 'note', label: 'Note' },
  { key: 'operatorEmail', label: 'Operator' },
  { key: 'appliedAtLabel', label: 'Applied' },
];

function pct(numerator, denominator) {
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

const SIGNAL_LABEL = {
  negative_balance: 'Negative balance',
  topup_failed: 'Top-up failed',
  pool_empty: 'Pool empty',
  expiring_soon: 'Expiring soon',
};
const SIGNAL_BADGE_COLOR = {
  negative_balance: 'red',
  topup_failed: 'red',
  pool_empty: 'orange',
  expiring_soon: 'orange',
};

function Tile({ icon, label, children }) {
  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon image={icon} color="grey" size={14} />
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 14, color: '#111827' }}>{children}</div>
    </div>
  );
}

export function Credits() {
  const navigate = useNavigate();
  const portfolio = useAPI('/api/credit/portfolio');
  const attention = useAPI('/api/credit/needs-attention');
  const adjustments = useAPI('/api/credit/adjustments');
  const data = portfolio.data;
  const [signalFilter, setSignalFilter] = useState('');
  const signalRows = (attention.data || []).filter((r) => !signalFilter || r.signal === signalFilter);
  const adjustmentEntries = adjustments.data?.entries || [];
  const windowDays = adjustments.data?.windowDays ?? 7;
  const adjustmentsTotal = adjustments.data?.total ?? adjustmentEntries.length;
  const adjustmentsTruncated = Boolean(adjustments.data?.truncated);
  const adjustmentsCountLabel = adjustmentsTruncated
    ? `showing the most recent ${adjustmentEntries.length} of ${adjustmentsTotal} entries`
    : `${adjustmentEntries.length} entries`;

  function exportAdjustmentsCsv() {
    const csvRows = adjustmentEntries.map((row) => ({
      ...row,
      targetLabel: row.target.type === 'pool' ? 'Workspace pool' : `${row.target.name || ''} <${row.target.email || ''}>`,
      appliedAtLabel: convertToLocalTime(row.appliedAt),
    }));
    downloadCsv('credit-adjustments.csv', rowsToCsv(csvRows, ADJUSTMENTS_CSV_COLUMNS));
  }

  const bundleAdoptionPct = data ? pct(data.bundleAdoption.accounts, data.bundleAdoption.paidAccounts) : '—';
  const autoTopupPct = data ? pct(data.autoTopupEnabled.accounts, data.autoTopupEnabled.buyerAccounts) : '—';
  const consumedTotal = data?.consumedThisMonth?.total ?? 0;

  const rows = [
    [
      { icon: 'credit-card', label: 'Bundle adoption', render: () => (
        <span><span style={{ fontSize: 22, fontWeight: 700 }}>{bundleAdoptionPct}</span>{' '}
          <span style={{ color: '#6b7280', fontSize: 13 }}>{data ? `${data.bundleAdoption.accounts} of ${data.bundleAdoption.paidAccounts} paid accounts · target ${data.bundleAdoption.targetPct}%` : '—'}</span></span>
      ) },
      { icon: 'refresh-cw', label: 'Auto top-up enabled', render: () => (
        <span><span style={{ fontSize: 22, fontWeight: 700 }}>{autoTopupPct}</span>{' '}
          <span style={{ color: '#6b7280', fontSize: 13 }}>{data ? `${data.autoTopupEnabled.accounts} of ${data.autoTopupEnabled.buyerAccounts} buyers · target ${data.autoTopupEnabled.targetPct}%` : '—'}</span></span>
      ) },
      { icon: 'dollar-sign', label: 'Credit revenue', render: () => (
        <span style={{ color: '#9ca3af' }}>Not available in Mission Control yet</span>
      ) },
    ],
    [
      { icon: 'bar-chart-2', label: 'Credits consumed', render: () => (
        <span><span style={{ fontSize: 22, fontWeight: 700 }}>{consumedTotal.toLocaleString()}</span>{' '}
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            {consumedTotal > 0
              ? `deep research ${pct(data.consumedThisMonth.deepResearch, consumedTotal)} · meeting notes ${pct(data.consumedThisMonth.meetingNotes, consumedTotal)}`
              : 'no consumption yet this month'}
          </span></span>
      ) },
      { icon: 'clock', label: 'Expiring within 14 days', render: () => (
        <span><span style={{ fontSize: 22, fontWeight: 700, color: '#f97316' }}>{(data?.expiringSoon.credits ?? 0).toLocaleString()}</span>{' '}
          <span style={{ color: '#6b7280', fontSize: 13 }}>unused, across {data?.expiringSoon.accounts ?? 0} accounts</span></span>
      ) },
      { icon: 'alert-triangle', label: 'Negative balances', render: () => (
        <span><span style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>{(data?.negativeBalances.accounts ?? 0).toLocaleString()}</span>{' '}
          <span style={{ color: '#6b7280', fontSize: 13 }}>accounts · {(data?.negativeBalances.totalDeficit ?? 0).toLocaleString()} credits owed</span></span>
      ) },
    ],
  ];

  return (
    <Animate type='pop'>

      {/* Page header */}
      <div style={{ marginBottom: 28, fontFamily: "'Open Sans', sans-serif" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px', lineHeight: 1.3 }}>
          <span style={{ color: '#6b7280', fontWeight: 700 }}>Revenue</span> / Pro Credits
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
          Monitor credit balances, bundle expiry and manual grants across all accounts
        </p>
      </div>

      {/* Summary line */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: '12px 20px', marginBottom: 16,
      }}>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          <span style={{ fontWeight: 600, color: '#111827' }}>{(data?.accountsWithCredits ?? 0).toLocaleString()}</span> accounts with credits
          {' · '}
          <span style={{ fontWeight: 600, color: '#111827' }}>{(data?.soldThisMonth ?? 0).toLocaleString()}</span> sold this month
          {' · '}
          <span style={{ fontWeight: 600, color: '#dc2626' }}>{(data?.blockedAccounts ?? 0).toLocaleString()}</span> blocked
          {' · '}
          <span style={{ fontWeight: 600, color: '#f97316' }}>{(data?.expiringSoonAccounts ?? 0).toLocaleString()}</span> expiring within 14 days
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          {adjustmentsTruncated && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              Export includes the most recent {adjustmentEntries.length} of {adjustmentsTotal}
            </span>
          )}
          <button
            onClick={exportAdjustmentsCsv}
            disabled={adjustmentEntries.length === 0}
            title="Export the recent manual adjustments below"
            style={{
              padding: '6px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: '1px solid #e5e7eb',
              background: adjustmentEntries.length === 0 ? '#f3f4f6' : '#fff',
              color: adjustmentEntries.length === 0 ? '#9ca3af' : '#374151',
              cursor: adjustmentEntries.length === 0 ? 'default' : 'pointer',
            }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {rows.map((row, rowIdx) => (
          <Fragment key={rowIdx}>
            {rowIdx > 0 && <div style={{ height: 1, background: '#f3f4f6' }} />}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
              {row.map((tile, colIdx) => {
                const cell = <Tile icon={tile.icon} label={tile.label}>{tile.render()}</Tile>;
                return colIdx === 1
                  ? <div key={colIdx} style={{ borderLeft: '1px solid #f3f4f6', borderRight: '1px solid #f3f4f6' }}>{cell}</div>
                  : <Fragment key={colIdx}>{cell}</Fragment>;
              })}
            </div>
          </Fragment>
        ))}
      </div>

      {/* Needs attention */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Needs attention</span>
          <select
            value={signalFilter}
            onChange={(e) => setSignalFilter(e.target.value)}
            style={{ height: 36, padding: '0 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer', minWidth: 220 }}
          >
            <option value="">All signals</option>
            {Object.entries(SIGNAL_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        {signalRows.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
            {attention.loading ? 'Loading…' : 'No accounts currently need attention'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Account', 'Signal', 'Detail', 'Pool balance', 'Detected', 'Actions'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {signalRows.map((row, i) => (
                <tr key={`${row.accountId}-${row.signal}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
                    <div>{row.accountName || row.accountId}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400, marginTop: 2 }}>{row.seats} users · {row.region || '—'} · {row.plan || '—'}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge text={SIGNAL_LABEL[row.signal] || row.signal} color={SIGNAL_BADGE_COLOR[row.signal] || 'blue'} />
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{row.detail}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: row.poolBalance < 0 ? '#dc2626' : '#111827' }}>{row.poolBalance.toLocaleString()}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{row.detectedAt ? convertToLocalTime(row.detectedAt) : '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <button
                      onClick={() => navigate(`/accounts/${row.accountId}/credits`)}
                      style={{ fontSize: 12, color: '#1974f9', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'Open Sans', sans-serif" }}
                    >
                      View credits
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent manual adjustments */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Recent manual adjustments</span>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Last {windowDays} days · {adjustmentsCountLabel}</span>
        </div>
        {adjustmentEntries.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
            {adjustments.loading ? 'Loading…' : `No manual adjustments in the last ${windowDays} days`}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Account', 'Target', 'Credits', 'Reason', 'Operator', 'Applied'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adjustmentEntries.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#111827', fontWeight: 500 }}>{row.accountName || row.accountId}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {row.target.type === 'pool' ? (
                      <Badge text="Workspace pool" color="blue" />
                    ) : (
                      <div>
                        <div style={{ fontSize: 13, color: '#111827' }}>{row.target.name || '—'}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>{row.target.email}</div>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: row.amount < 0 ? '#dc2626' : '#16a34a' }}>
                    {row.amount > 0 ? '+' : ''}{row.amount.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>
                    <div>{row.reasonLabel}</div>
                    {row.note && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{row.note}</div>}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{row.operatorEmail || '—'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{convertToLocalTime(row.appliedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Animate>
  );
}
